import A from './types';
import get from 'lodash/get';
import flatten from 'lodash/flatten';
import omit from 'lodash/omit';
import same from 'lodash/identity';
import keyBy from 'lodash/keyBy';
import addDays from 'date-fns/add_days';

import * as searchHelper from '~/misc/search';
import resourceId, {getResource} from '~/misc/resourceId';
import create from '~/misc/create';
import RESOURCE from '~/modules/resources/types';
import loadsURL from '~/modules/loadsSearch/url';
import loadFromState from '~/misc/loadFromState';
import config from '~/config';

const initialState = {
  index: {},
  active: {},
};

const loadToQueryLocation = ({address, location}) => ({
  // structure of query pickup/dropoff location differ from load pickup/dropoff location
  location,
  description: {
    city: address && address.city,
    state: address && address.state,
  },
});


const addDeadhead = (location) => {
  if (location.location_state) {
    return location;
  }

  return {
    ...location,
    deadhead: 200,
  };
};

const withLoads = (dates, item) => {
  const formQuery = searchHelper.fromServerLegacy({...item.query, limit: null, dates});
  return {
    ...item,
    dates,
    formQuery,
    loadsIdentity: resourceId('queriedLoads', {query: item.query}),
    url: loadsURL(formQuery),
  };
};

const okToUsePickup = (pickup, usedState) => {
  if (!pickup) {
    return false;
  }

  if (config.backhaulDirections2) {
    return true;
  }

  if (!pickup.description.city) {
    if (pickup.description.state === usedState || pickup.location_state === usedState) {
      return false;
    }
    return false;
  }

  return true;
};

const getDirections = (identity, load, dates, dayType, originalQuery) => {
  const loadDestination = loadToQueryLocation(load.original.drop_off);
  const backhaulPickup = addDeadhead(loadDestination);
  const destinationState = loadDestination.description.state;

  if (!originalQuery) {
    return [
      withLoads(dates, {
        // loads from load destination to "anywhere"
        identity: resourceId('backhaulDirection', {directionName: 'anywhere', dayType}, identity),
        query: {
          dates,
          offset: 0,
          pick_up: backhaulPickup,
          drop_off: null,
          limit: 1,
        },
      }),
      !config.backhaulDirections2 && withLoads(dates, {
        // loads from load destination to "same state as destination"
        identity: resourceId('backhaulDirection', {directionName: 'loadPickupState', dayType}, identity),
        query: {
          dates,
          offset: 0,
          pick_up: backhaulPickup,
          drop_off: {
            description: {
              state: destinationState,
            },
            location_state: destinationState,
          },
          limit: 1,
        },
      }),
      withLoads(dates, {
        // loads from load destination to "load pickup"
        identity: resourceId('backhaulDirection', {directionName: 'loadPickup', dayType}, identity),
        query: {
          dates,
          offset: 0,
          pick_up: backhaulPickup,
          drop_off: {
            ...loadToQueryLocation(load.original.pickup),
            deadhead: 100,
          },
          limit: 1,
        },
      }),
    ];
  }

  return [
    withLoads(dates, {
      // loads from load destination to "anywhere"
      identity: resourceId('backhaulDirection', {directionName: 'anywhere', dayType}, identity),
      query: {
        ...originalQuery,
        dates,
        dots: null,
        offset: 0,
        pick_up: backhaulPickup,
        drop_off: null,
        limit: 1,
      },
    }),
    !config.backhaulDirections2 && withLoads(dates, {
      // loads from load destination to "same state as destination"
      identity: resourceId('backhaulDirection', {directionName: 'samestate', dayType}, identity),
      query: {
        ...originalQuery,
        dates,
        dots: null,
        offset: 0,
        pick_up: backhaulPickup,
        drop_off: {
          description: {
            state: destinationState,
          },
          location_state: destinationState,
        },
        limit: 1,
      },
    }),
    config.backhaulDirections2 && withLoads(dates, {
      // loads from load destination to "load pickup"
      identity: resourceId('backhaulDirection', {directionName: 'loadPickup', dayType}, identity),
      query: {
        dates,
        offset: 0,
        pick_up: backhaulPickup,
        drop_off: {
          ...loadToQueryLocation(load.original.pickup),
          deadhead: 100,
        },
        limit: 1,
      },
    }),
    okToUsePickup(originalQuery.pick_up, destinationState) && withLoads(dates, {
      // loads from load destination to "user query origin"
      identity: resourceId('backhaulDirection', {directionName: 'originalQuery', dayType}, identity),
      query: {
        ...originalQuery,
        dates,
        dots: null,
        offset: 0,
        pick_up: backhaulPickup,
        drop_off: addDeadhead(originalQuery.pick_up),
        limit: 1,
      },
    }),
  ];
};

const createBackhaulByIdentity = (identity, fullState) => {
  const {loadId, originanFormQuery} = identity.props;

  // query bellow could be relaxed query for similarLoads (or something else, related to user query)
  // so it could differ from query which was filled by user

  const load = loadFromState(fullState, identity.props.loadIdentity);
  const loadOrigin = loadToQueryLocation(load.original.pickup);
  const loadDestination = loadToQueryLocation(load.original.drop_off);

  const backhaulPickup = addDeadhead(loadDestination);

  // we want to get query which was filled by user
  const originalQuery = originanFormQuery && searchHelper.toServerLegacy(originanFormQuery);

  const sameDay = new Date(load.from_date_local);
  const nextDay = addDays(sameDay, 1);

  const sameDayList = getDirections(identity, load, [sameDay], 'sameDay', originalQuery)
    .filter(same);
  const nextDayList = getDirections(identity, load, [nextDay], 'nextDay', originalQuery)
    .filter(same);

  const lists = [
    sameDayList,
    nextDayList,
  ].filter(item => (item && item.length));

  const all = sameDayList.concat(nextDayList);

  return {
    identity: identity,
    load: {
      id: loadId,
      origin: loadOrigin,
      destination: loadDestination,
    },
    pick_up: backhaulPickup,
    originalQuery,
    originanFormQuery,
    all,
    lists,
    index: keyBy(all, 'identity.shortkey'),
    bookingLoad: null,
  };
};

const reducer = ({index, active, ...state}, action, fullState) => {
  switch (action.type) {
    case A.BACKHAUL_TOGGLED: {
      const key = action.payload;
      if (active[key]) {
        return {
          ...state,
          active: omit(active, key),
        };
      }

      return {
        ...state,
        active: {
          ...active,
          [key]: true,
        },
      };
    }

    case RESOURCE.RESOURCE_REQUESTED: {
      const name = action.payload.name;
      if (name !== 'backhaul') {
        return null;
      }

      const backhaul = createBackhaulByIdentity(action.payload, fullState);

      const actionsQueue = backhaul.all.map(item => create(RESOURCE.RESOURCE_NEEDED, {
        name: 'queriedLoads',
        need: item.loadsIdentity,
        needy: item.identity,
      }));

      return {
        ...state,
        index: {
          ...index,
          [action.payload.key]: backhaul,
        },
        actionsQueue,
      };
    }

    case RESOURCE.RESOURCE_DISPOSED: {
      const name = action.payload.name;
      if (name !== 'backhaul') {
        return null;
      }

      const key = action.payload.key;
      const backhaul = index[key];

      const actionsQueue = backhaul.all.map(item => create(RESOURCE.RESOURCE_NOT_NEEDED, {
        name: 'queriedLoads',
        need: item.loadsIdentity,
        needy: item.identity,
      }));

      return {
        ...state,
        index: omit(index, key),
        actionsQueue,
      };
    }

    case RESOURCE.RESOURCE_UPDATED_DEP: {
      const {needy} = action.payload;
      const name = needy.name;
      if (name !== 'backhaulDirection') {
        return null;
      }

      const backhaulIdentity = needy.parentIdentity;
      const backhaul = index[backhaulIdentity.key];

      const resourses = backhaul.all
        .map(item => getResource(fullState, item.loadsIdentity))
        .filter(item => item);

      const loading = Boolean(resourses.some(item => !item.completed));

      const allLoads = !loading && flatten(resourses.map(loadsResource => get(loadsResource, 'item.shipments')))
        .filter(item => item && item.book_now);

      const selected = (allLoads && allLoads[allLoads.length - 1]) || null;

      return {
        index: {
          ...index,
          [backhaulIdentity.key]: {
            ...backhaul,
            bookingLoad: selected,
            bookingLoadLoading: loading,
          },
        },
        actionsQueue: [selected && create(A.BACKHAUL_BOOKINGLOAD_REVEALED, {
          bookingLoad: selected.identity,
          expandedLoad: backhaulIdentity.props.loadIdentity,
        })],
      };
    }
  }


  return null;
};

export default {reducer, initialState};



// WEBPACK FOOTER //
// ./src/modules/backhaul/reducer.js